import { completeCafWithPhoto } from '../compliance'
// web/src/services/firestore/cafService.js
import { db } from '@/firebase'
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction,
  where,
} from 'firebase/firestore'

// Normalize one doc -> the shape your UI expects
function mapDoc(d) {
  const data = d.data() || {}

  // ---- FILES NORMALIZATION (back-compat across keys) ----
  const files =
    Array.isArray(data.files) && data.files.length ? data.files :
    Array.isArray(data.documents) && data.documents.length ? data.documents :
    Array.isArray(data.docs) && data.docs.length ? data.docs :
    Array.isArray(data.attachments) && data.attachments.length ? data.attachments :
    []

  return {
    id: d.id,

    // core
    number: data.number || '',
    title: data.title || '',
    status: data.status || '',
    stage: data.stage || '',
    communityCode: data.communityCode || '',
    communityName: data.communityName || data.community || '',
    facilityType: data.facilityType || '',
    contractPrice: data.contractPrice ?? null,
    notBudgeted: data.notBudgeted === true,
    isAmendment: data.isAmendment === true,
    parentCafId: data.parentCafId || '',
    parentCafNumber: data.parentCafNumber || '',
    rootCafId: data.rootCafId || '',
    rootCafNumber: data.rootCafNumber || '',
    amendmentNumber:
      typeof data.amendmentNumber === 'number' ? data.amendmentNumber : null,
    amendmentReason: data.amendmentReason || '',
    originalApprovedAmount:
      data.originalApprovedAmount ??
      data.parentApprovedAmount ??
      null,
    amendmentDeltaAmount:
      data.amendmentDeltaAmount ??
      data.changeAmount ??
      null,
    revisedTotalAmount:
      data.revisedTotalAmount ??
      data.contractPrice ??
      null,
    activeAmendmentId: data.activeAmendmentId || '',
    activeAmendmentNumber:
      typeof data.activeAmendmentNumber === 'number' ? data.activeAmendmentNumber : null,
    activeAmendmentTitle: data.activeAmendmentTitle || '',
    activeAmendmentStatus: data.activeAmendmentStatus || '',
    completedViaAmendmentId: data.completedViaAmendmentId || '',
    completedViaAmendmentNumber:
      typeof data.completedViaAmendmentNumber === 'number' ? data.completedViaAmendmentNumber : null,
    completedViaAmendmentAt: data.completedViaAmendmentAt || null,
    currentAuthorizedTotal:
      data.currentAuthorizedTotal ??
      data.revisedAuthorizedTotal ??
      null,
    rolledUpActualAmount:
      data.rolledUpActualAmount ??
      data.projectActualRollup ??
      null,

    // project completion (NEW – make these visible to the UI)
    projectBudget:
      data.projectBudget ??
      data.projectBudgetAmount ??
      data.projectBudgetCost ??
      null,
    projectActual:
      data.projectActual ??
      data.projectActualAmount ??
      data.actualProjectAmount ??
      null,
    projectCompletionDate:
      data.projectCompletionDate ??
      data.completionDate ??
      '',
    voidReason: data.voidReason || '',
    voidedAt: data.voidedAt || null,
    voidedBy: data.voidedBy || '',

    // creator/meta
    createdByEmail: data.createdByEmail || '',
    createdByName: data.createdByName || '',

    // embedded sections
    messages: Array.isArray(data.messages) ? data.messages : [],
    proposals: Array.isArray(data.proposals) ? data.proposals : [],
    proposalsCount:
      typeof data.proposalsCount === 'number'
        ? data.proposalsCount
        : (Array.isArray(data.proposals) ? data.proposals.length : 0),
    approvals: data.approvals || { sequence: [], history: [] },
    timeline: Array.isArray(data.timeline) ? data.timeline : [],
    tags: Array.isArray(data.tags) ? data.tags : [],

    // files (canonical, always present)
    files,

    // workflow helpers
    startedStage: data.startedStage || data.initialStage || '',

    // timestamps
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  }
}

/* ------------------------------------------------------------------ */
/* LIST / SNAPSHOT HELPERS                                            */
/* ------------------------------------------------------------------ */

/** One-shot list (non-realtime) */
export async function listCafs() {
  const q = query(collection(db, 'caf'), orderBy('createdAt', 'desc'))
  const snap = await getDocs(q)
  const rows = snap.docs.map(mapDoc)
  if (import.meta.env.DEV) {
    console.log('[cafService] listCafs ->', rows.length, 'rows')
  }
  return rows
}

/** Realtime list subscription (optional) */
export function onCafsSnapshot(callback) {
  const q = query(collection(db, 'caf'), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map(mapDoc)
    if (import.meta.env.DEV) {
      console.log('[cafService] onCafsSnapshot ->', rows.length, 'rows')
    }
    callback(rows)
  })
}

/** Get single CAF once */
export async function getCaf(id) {
  const ref = doc(db, 'caf', id)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return mapDoc(snap)
}

/** Realtime single CAF (unused now, but handy for dev) */
export function onCafSnapshot(id, callback) {
  const ref = doc(db, 'caf', id)
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      callback(null)
      return
    }
    callback(mapDoc(snap))
  })
}

/* ------------------------------------------------------------------ */
/* CAF NUMBER GENERATOR (SEQUENTIAL, PER COMMUNITY + YEAR)            */
/* ------------------------------------------------------------------ */
/**
 * Uses a /sequences/{seqId} doc to generate:
 *   CAF-YYYY-COMMUNITYCODE-0001, 0002, ...
 *
 * arg can be:
 *   - 'NOBLEP'
 *   - { communityCode: 'NOBLEP' }
 *   - { communityName: 'Noble Plaza', code: 'NOBLEP' } etc.
 */
export async function getNextCafNumberFS(arg) {
  let communityCode = ''

  if (typeof arg === 'string') {
    communityCode = arg
  } else if (arg && typeof arg === 'object') {
    communityCode =
      arg.communityCode ||
      arg.code ||
      arg.community ||
      arg.communityName ||
      ''
  }

  const year = new Date().getFullYear()
  const cc = String(communityCode || 'GEN')
    .toUpperCase()
    .replace(/\s+/g, '')

  const seqId = `CAF-${year}-${cc}`
  const seqRef = doc(db, 'sequences', seqId)

  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(seqRef)
    const cur =
      snap.exists() && typeof snap.data().next === 'number'
        ? snap.data().next
        : 0
    const updated = cur + 1
    tx.set(
      seqRef,
      {
        next: updated,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
    return updated
  })

  const suffix = String(next).padStart(4, '0')
  return `CAF-${year}-${cc}-${suffix}`
}

/* ------------------------------------------------------------------ */
/* CREATE / UPDATE / DELETE                                            */
/* ------------------------------------------------------------------ */

/** Create CAF (doc ID = CAF number) */
export async function createCaf(payload) {
  const now = serverTimestamp()

  const initialFiles =
    Array.isArray(payload.files) ? payload.files :
    Array.isArray(payload.documents) ? payload.documents :
    Array.isArray(payload.docs) ? payload.docs :
    Array.isArray(payload.attachments) ? payload.attachments :
    []

  // Ensure we have a CAF number; if caller didn't provide one, generate it.
  let cafNumber = payload.number
  if (!cafNumber) {
    cafNumber = await getNextCafNumberFS(
      payload.communityCode ||
      payload.community ||
      payload.communityName ||
      ''
    )
  }

  const id = cafNumber // Firestore doc id == CAF number
  const ref = doc(db, 'caf', id)

  await setDoc(ref, {
    ...payload,
    number: cafNumber,
    createdAt: now,
    updatedAt: now,

    // ensure embedded arrays exist even if UI adds later
    messages: Array.isArray(payload.messages) ? payload.messages : [],
    proposals: Array.isArray(payload.proposals) ? payload.proposals : [],
    approvals: payload.approvals || { sequence: [], history: [] },
    timeline: Array.isArray(payload.timeline) ? payload.timeline : [],

    // write to all common keys to be friendly with any readers
    files: initialFiles,
    documents: initialFiles,
    docs: initialFiles,
    attachments: initialFiles,
    proposalsCount:
      typeof payload.proposalsCount === 'number'
        ? payload.proposalsCount
        : (Array.isArray(payload.proposals) ? payload.proposals.length : 0),
  })

  return id
}

export async function listChildAmendmentsFS(parentCafId) {
  if (!parentCafId) return []
  const [directSnap, rootSnap] = await Promise.all([
    getDocs(query(collection(db, 'caf'), where('parentCafId', '==', parentCafId))),
    getDocs(query(collection(db, 'caf'), where('rootCafId', '==', parentCafId))),
  ])
  const byId = new Map()
  for (const row of [...directSnap.docs, ...rootSnap.docs].map(mapDoc)) {
    byId.set(row.id, row)
  }
  return Array.from(byId.values())
    .sort((a, b) => {
      const aNum = Number(a.amendmentNumber || 0)
      const bNum = Number(b.amendmentNumber || 0)
      if (aNum !== bNum) return aNum - bNum
      const aTs = a.createdAt?.seconds ? a.createdAt.seconds : 0
      const bTs = b.createdAt?.seconds ? b.createdAt.seconds : 0
      return aTs - bTs
    })
}

export async function createAmendmentFromCafFS(parentCaf, {
  reason = '',
  createdByEmail = '',
  createdByName = '',
} = {}) {
  if (!parentCaf?.id) throw new Error('Parent CAF is required.')

  const parentId = parentCaf.id
  const rootCafId = parentCaf.rootCafId || parentCaf.parentCafId || parentId
  const rootCafNumber = parentCaf.rootCafNumber || parentCaf.parentCafNumber || parentCaf.number || parentId

  const existingAmendmentsSnap = await getDocs(
    query(collection(db, 'caf'), where('rootCafId', '==', rootCafId)),
  )
  const amendmentNumber = existingAmendmentsSnap.size + 1
  const number = await getNextCafNumberFS(parentCaf.communityCode || parentCaf.communityName || '')

  const timelineUser = createdByName || createdByEmail || 'user'
  const parentProposals = Array.isArray(parentCaf.proposals) ? parentCaf.proposals : []
  const inheritedProposals = parentProposals.filter((proposal) => proposal?.recommended)
  const newCaf = {
    number,
    communityCode: parentCaf.communityCode || '',
    communityName: parentCaf.communityName || '',
    title: parentCaf.title || '',
    facilityType: parentCaf.facilityType || '',
    contractPrice: parentCaf.contractPrice ?? null,
    originalApprovedAmount: parentCaf.contractPrice ?? null,
    amendmentDeltaAmount: 0,
    revisedTotalAmount: parentCaf.contractPrice ?? null,
    projectBudget: parentCaf.projectBudget ?? parentCaf.contractPrice ?? null,
    projectActual: null,
    projectCompletionDate: parentCaf.projectCompletionDate || '',
    notBudgeted: parentCaf.notBudgeted === true,
    completionDateReminder: {
      sentForDate: '',
      sentAt: null,
      sentTo: [],
    },
    status: 'draft',
    stage: 'Manager',
    proposals: inheritedProposals,
    proposalsCount: inheritedProposals.length,
    messages: [],
    files: [],
    docs: [],
    documents: [],
    attachments: [],
    approvals: {
      sequence: ['Manager', 'Regional Manager'],
      history: [],
    },
    tags: Array.isArray(parentCaf.tags) ? parentCaf.tags : [],
    isAmendment: true,
    parentCafId: parentId,
    parentCafNumber: parentCaf.number || parentId,
    rootCafId,
    rootCafNumber,
    amendmentNumber,
    amendmentReason: String(reason || '').trim(),
    createdByEmail,
    createdByName,
    timeline: [
      {
        ts: new Date().toISOString(),
        event: `CAF amendment created from ${parentCaf.number || parentId} (Amendment ${amendmentNumber})${reason ? ` — ${reason}` : ''} — ${timelineUser}`,
      },
    ],
  }

  const amendmentId = await createCaf(newCaf)

  const rootRef = doc(db, 'caf', rootCafId)
  const rootSnap = await getDoc(rootRef)
  if (rootSnap.exists()) {
    const rootData = rootSnap.data() || {}
    await updateDoc(rootRef, {
      activeAmendmentId: amendmentId,
      activeAmendmentNumber: amendmentNumber,
      activeAmendmentTitle: parentCaf.title || '',
      activeAmendmentStatus: 'draft',
      currentAuthorizedTotal: parentCaf.contractPrice ?? null,
      completedViaAmendmentId: '',
      completedViaAmendmentNumber: null,
      completedViaAmendmentAt: null,
      rolledUpActualAmount: null,
      timeline: [
        ...(Array.isArray(rootData.timeline) ? rootData.timeline : []),
        {
          ts: new Date().toISOString(),
          event: `Amendment ${amendmentNumber} opened for revised scope/budget closeout: ${number}${reason ? ` — ${reason}` : ''} — ${timelineUser}`,
        },
      ],
      updatedAt: serverTimestamp(),
    })
  }

  const rootTaskSnap = await getDocs(query(collection(db, 'tasks'), where('cafId', '==', rootCafId)))
  if (!rootTaskSnap.empty) {
    const rootTaskDocs = rootTaskSnap.docs.sort((a, b) => {
      const aT = a.data()?.createdAt?.seconds || 0
      const bT = b.data()?.createdAt?.seconds || 0
      return bT - aT
    })
    await updateDoc(rootTaskDocs[0].ref, {
      status: 'complete',
      note: `Superseded by Amendment ${amendmentNumber} (${number}) for revised scope, budget, and project completion.`,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }

  if (parentId !== rootCafId) {
    await updateDoc(doc(db, 'caf', parentId), {
      timeline: [
        ...(Array.isArray(parentCaf.timeline) ? parentCaf.timeline : []),
        {
          ts: new Date().toISOString(),
          event: `Amendment ${amendmentNumber} created: ${number}${reason ? ` — ${reason}` : ''} — ${timelineUser}`,
        },
      ],
      updatedAt: serverTimestamp(),
    })
  }

  return amendmentId
}

/**
 * Helper that decides what a CAF-linked task should
 * look like (type/title/note) for a given CAF status.
 */
function describeTaskForCafStatus(status, cafTitle, cafNumber) {
  const s = (status || '').toString().trim().toLowerCase()
  const label = cafTitle || cafNumber || ''
  const baseNum = cafNumber || ''

  switch (s) {
    case 'draft':
    case 'open':
      return {
        taskStatus: s || 'draft',
        type: 'draft',
        title: label ? `CAF Draft: ${label}` : 'CAF Draft',
        note: 'Draft CAF awaiting completion',
      }

    case 'submitted':
    case 'in_review':
      return {
        taskStatus: 'awaiting_approval',
        type: 'approval',
        title: label ? `CAF in review: ${label}` : 'CAF in review',
        note: 'Awaiting approvals',
      }

    case 'changes_requested':
      return {
        taskStatus: 'changes_requested',
        type: 'draft',
        title: label ? `CAF changes requested: ${label}` : 'CAF changes requested',
        note: 'Please revise and resubmit this CAF.',
      }

    case 'approved':
      return {
        taskStatus: 'approved',
        type: 'completion',
        title: label ? `Complete project for: ${label}` : 'Complete project setup',
        note: 'Enter final budget/actuals and complete the project.',
      }

    case 'void':
      return {
        taskStatus: 'closed',
        type: 'system',
        title: label ? `CAF Voided: ${label}` : 'CAF Voided',
        note: 'CAF voided — removed from workflow and reporting.',
      }

    default:
      return {
        taskStatus: s || 'open',
        type: 'draft',
        title: label ? `CAF: ${label}` : (baseNum ? `CAF ${baseNum}` : 'CAF task'),
        note: '',
      }
  }
}

/**
 * Update CAF (partial merge) and sync related tasks.
 *
 * For every task where tasks.cafId == this CAF id:
 *   - status is mirrored to the CAF status
 *   - title / note / type are updated based on CAF status
 *     so the task always describes the next action.
 */
export async function updateCaf(id, changes) {
  const cafRef = doc(db, 'caf', id)

  // Grab previous state so we can fill in title/number if not in changes
  let prevData = null
  try {
    const prevSnap = await getDoc(cafRef)
    if (prevSnap.exists()) prevData = prevSnap.data()
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[cafService] failed to read previous CAF before update', id, err)
    }
  }

  // 1) update the CAF itself
  if (changes.status === 'complete') {
    await completeCafWithPhoto({ id, projectActual: changes.projectActual ?? prevData?.projectActual })
  } else {
    await updateDoc(cafRef, { ...changes, updatedAt: serverTimestamp() })
  }

  const newStatus = changes.status ?? prevData?.status
  if (!newStatus) return

  // Figure out title/number to feed into the description helper
  const cafTitle = changes.title ?? prevData?.title ?? ''
  const cafNumber = changes.number ?? prevData?.number ?? id

  // 2) update any tasks linked to this CAF
  try {
    const tasksCol = collection(db, 'tasks')
    const qTasks = query(tasksCol, where('cafId', '==', id))
    const snap = await getDocs(qTasks)
    const now = serverTimestamp()

    const { taskStatus, type, title, note } =
      describeTaskForCafStatus(newStatus, cafTitle, cafNumber)

    const updates = snap.docs.map((taskDoc) =>
      updateDoc(taskDoc.ref, {
        status: taskStatus || newStatus,
        type,
        title,
        note,
        updatedAt: now,
      })
    )

    await Promise.all(updates)

    if (import.meta.env.DEV) {
      console.log(
        '[cafService] updateCaf synced status + labels to',
        snap.size,
        'task(s) for CAF',
        id
      )
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[cafService] failed to sync tasks for CAF', id, err)
    }
    // CAF update already succeeded; don't throw
  }
}

/** Delete CAF */
export async function deleteCaf(id) {
  await deleteDoc(doc(db, 'caf', id))
}
